import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { acceptDriverRegistration } from "../../api/driver-registrations.api";
import "./accept-driver-registration.view.css";

type Status = "loading" | "success" | "error";

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ??
      error.message
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

export function AcceptDriverRegistrationView() {
  const { driverRegistrationId, organizationId } = useParams<{
    driverRegistrationId: string;
    organizationId: string;
  }>();
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!driverRegistrationId || !organizationId) {
      setStatus("error");
      setError("Missing driver registration or organization id.");
      return;
    }

    setStatus("loading");
    setError("");
    acceptDriverRegistration(driverRegistrationId, organizationId)
      .then(() => {
        setStatus("success");
      })
      .catch((error) => {
        setStatus("error");
        setError(getErrorMessage(error));
      });
  }, [driverRegistrationId, organizationId]);

  return (
    <div className="accept-driver-registration-view">
      {status === "loading" && (
        <div className="accept-loading" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span>Accepting Driver Registration...</span>
        </div>
      )}
      {status === "success" && (
        <div className="accept-success">
          <p className="accept-success-title">Driver registration accepted.</p>
          <p className="accept-success-note">
            An invite code has been sent to the driver.
          </p>
          <p className="accept-success-note">
            This window can now be closed safely.
          </p>
        </div>
      )}
      {status === "error" && (
        <p className="accept-error" role="alert">
          Failed to accept driver registration: {error}
        </p>
      )}
    </div>
  );
}
